import { useEffect, useMemo, useRef, useState } from "react";

import { fetchFiltersDataObject } from "../../../Helper/Table/TableHelper";
import { getDeclaredFilters, orderResolvedFilters } from "../filterHelpers";

// Resolves one table's declared filters into the `filters` prop
// GenericDraggableTableView takes.
//
// The resolution itself is the SHARED fetchFiltersDataObject, the same one
// every other filtered table in the product uses: its output is already
// { key: { ...declaration, values } }, which is exactly the prop shape, so
// there is nothing here to translate and deliberately no second resolver.
//
// What this adds is the three things a page needs around it: reading the
// declarations off metaInfo, a stable panel order, and not leaving a stale
// answer on screen when the declarations change or the component unmounts.
export const useConfiguredFilters = (metaInfo, tableKey) => {
    const [filters, setFilters] = useState({});
    const [isLoading, setIsLoading] = useState(false);

    // Re-read on every render, but only ACTED on when the content changes --
    // see the signature below. metaInfo arrives from usePageMeta and an org
    // with no filterConfigurations block yields [], which is the rollout case:
    // no panel, no request, no crash.
    const declarations = useMemo(
        () => getDeclaredFilters(metaInfo, tableKey),
        [metaInfo, tableKey]
    );

    // The effect is keyed on the declarations' CONTENT, not their identity.
    // pageMeta is re-created by its provider often enough that an identity
    // dependency would re-fire the whole option fetch on unrelated renders,
    // and these are small config objects.
    const signature = JSON.stringify(declarations);

    // The declarations the effect should use, without putting the array itself
    // in the dependency list (which would defeat the signature).
    const declarationsRef = useRef(declarations);
    declarationsRef.current = declarations;

    useEffect(() => {
        const current = declarationsRef.current;
        if (!current.length) {
            setFilters({});
            setIsLoading(false);
            return undefined;
        }

        let cancelled = false;
        setIsLoading(true);

        // Started from {} rather than from the filters already in state:
        // fetchFiltersDataObject skips any key it is handed, so passing the
        // previous answer would keep the OLD options for a declaration whose
        // query just changed.
        fetchFiltersDataObject(current, {})
            .then((resolved) => {
                if (cancelled) return;
                setFilters(orderResolvedFilters(resolved, current));
                setIsLoading(false);
            })
            .catch(() => {
                // A filter whose options could not be loaded is left OUT of the
                // panel rather than drawn empty: an empty dropdown reads as
                // "this org has none of these", which is a confident and wrong
                // answer. The list itself is unaffected.
                if (cancelled) return;
                setFilters({});
                setIsLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [signature]);

    return { filters, isLoading };
};

export default useConfiguredFilters;
